import { useState } from 'react'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { createWebinarTopicUpvotedNotification } from '@domain/features/user-notifications/user-notifications-feature'
import {
  deleteExistingWebinarSuggestion,
  submitWebinarSuggestion,
  toggleWebinarSuggestionLike,
  updateExistingWebinarSuggestion,
} from '@domain/features/webinar-suggestions/webinar-suggestions-feature'
import type { WebinarSuggestion } from '@domain/models/webinar-suggestions'

interface SuggestionsUserLike {
  uid: string
  isAdmin: boolean
  profileSlug?: string | null
}

interface UseWebinarSuggestionsActionsParams {
  user: SuggestionsUserLike | null
  onAuthRequired: () => void
  onSuggestionsChanged?: () => void
}

export function useWebinarSuggestionsActions({
  user,
  onAuthRequired,
  onSuggestionsChanged,
}: UseWebinarSuggestionsActionsParams) {
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false)
  const [editingSuggestion, setEditingSuggestion] = useState<WebinarSuggestion | null>(null)
  const [modalSubmitting, setModalSubmitting] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [activeLikeId, setActiveLikeId] = useState<string | null>(null)
  const [activeDeleteId, setActiveDeleteId] = useState<string | null>(null)

  const openCreateSuggestionModal = () => {
    if (!user) {
      onAuthRequired()
      return
    }
    setModalError(null)
    setEditingSuggestion(null)
    setIsSuggestionModalOpen(true)
  }

  const openEditSuggestionModal = (suggestion: WebinarSuggestion) => {
    setEditingSuggestion(suggestion)
    setModalError(null)
    setIsSuggestionModalOpen(true)
  }

  const closeSuggestionModal = () => {
    setIsSuggestionModalOpen(false)
    setEditingSuggestion(null)
    setModalError(null)
  }

  const handleSubmitSuggestion = async (input: {
    subject: string
    description: string
    adminReplyLabel?: string
  }) => {
    if (!user) {
      onAuthRequired()
      return
    }

    setModalSubmitting(true)
    setModalError(null)

    try {
      if (editingSuggestion) {
        await updateExistingWebinarSuggestion(editingSuggestion.uid, {
          subject: input.subject,
          description: input.description,
          adminReplyLabel: input.adminReplyLabel,
        })
      } else {
        await submitWebinarSuggestion({
          subject: input.subject,
          description: input.description,
          adminReplyLabel: input.adminReplyLabel,
        })
      }
      const screenPath = `${window.location.pathname}${window.location.search || ''}`
      AnalyticsFeature.trackWebinarSuggestionSubmit(
        screenPath,
        'webinar_suggestions',
        Boolean(user),
      )
      onSuggestionsChanged?.()
      closeSuggestionModal()
    } catch (submitError) {
      setModalError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to save webinar suggestion.',
      )
    } finally {
      setModalSubmitting(false)
    }
  }

  const handleToggleSuggestionLike = async (
    suggestionId: string,
    submittedByUid?: string,
    suggestionTitle?: string,
  ) => {
    if (!user) {
      onAuthRequired()
      return
    }

    setActionError(null)
    setActiveLikeId(suggestionId)
    try {
      const didLike = await toggleWebinarSuggestionLike(suggestionId)
      if (didLike && user?.uid && submittedByUid) {
        void createWebinarTopicUpvotedNotification({
          forUserId: submittedByUid,
          senderId: user.uid,
          senderSlug: user.profileSlug ?? undefined,
          suggestionId,
          suggestionTitle,
        })
      }
      onSuggestionsChanged?.()
    } catch (toggleLikeError) {
      setActionError(
        toggleLikeError instanceof Error
          ? toggleLikeError.message
          : 'Failed to update webinar suggestion like.',
      )
    } finally {
      setActiveLikeId(null)
    }
  }

  const handleDeleteSuggestion = async (suggestion: WebinarSuggestion) => {
    if (!user) {
      return
    }

    const canManageSuggestion = user.isAdmin || user.uid === suggestion.submittedByUid
    if (!canManageSuggestion) {
      return
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete this webinar suggestion?',
    )
    if (!confirmed) {
      return
    }

    setActionError(null)
    setActiveDeleteId(suggestion.uid)
    try {
      await deleteExistingWebinarSuggestion(suggestion.uid)
      onSuggestionsChanged?.()
    } catch (deleteError) {
      setActionError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Failed to delete webinar suggestion.',
      )
    } finally {
      setActiveDeleteId(null)
    }
  }

  return {
    isSuggestionModalOpen,
    editingSuggestion,
    modalSubmitting,
    modalError,
    actionError,
    activeLikeId,
    activeDeleteId,
    openCreateSuggestionModal,
    openEditSuggestionModal,
    closeSuggestionModal,
    handleSubmitSuggestion,
    handleToggleSuggestionLike,
    handleDeleteSuggestion,
  }
}
